import { useCallback, useEffect, useMemo, useState } from "react";
import "./Pagamento.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import { Spinner } from "react-bootstrap";
import PageLoading from "components/PageLoading";

import DadosSindicato from "./PagamentoComponents/DadosSindicato/DadosSindicato";
import TabelaReembolso from "./PagamentoComponents/TabelaReembolso/TabelaReembolso";
import OutrasDeducoes from "./PagamentoComponents/OutrasDeducoes/OutrasDeducoes";
import ParcelaDados from "./PagamentoComponents/ParcelaDados/ParcelaDados";
import AcoesFormulario from "./PagamentoComponents/AcoesFormulario/AcoesFormulario";

import NotificationToast from "../../../components/NotificationToast/NotificationToast";
import {
  downloadPagamentoComprovanteDespesasArquivoAssinado,
  gerarPagPagamentoDespesasAvaliacao,
  getPagamentoComprovanteDespesasById,
} from "../../../services/pagamentoAvaliacaoService";
import type { PaymentDetail } from "../shared/paymentListTypes";
import { mapPagamentoComprovanteToDetail } from "../shared/mapPagamentoDetalhe";
import { formatSolicitacao } from "../shared/paymentListUtils";
import { PATHS } from "../../../routes/paths";

const normalize = (s: unknown) =>
  String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const formatCurrency = (v: number) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const Pagamento = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { idSolicitacao } = useParams();

  const [detalhe, setDetalhe] = useState<PaymentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [erro, setErro] = useState<unknown>(null);
  const [downloadingForm, setDownloadingForm] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [formPagamento, setFormPagamento] = useState({
    descricao: "",
    condicao: "10 dias",
    condicaoPagamentoId: 0,
  });

  const showMsg = (message: string, type = "success") => {
    setToastType(type);
    setToastMessage(message);
    setShowToast(true);
  };

  const readOnlyFromList = useMemo(() => {
    return !!location?.state?.readOnly;
  }, [location]);

  const pagamentoTravado = useMemo(() => {
    const s = normalize(detalhe?.situacaoPagamento);
    return (
      s === "em aprovacao no fusion" ||
      s === "pagamento provisionado" ||
      s === "pagamento concluido" ||
      s === "pagamento concluído" ||
      Boolean(detalhe?.numeroPAG)
    );
  }, [detalhe]);

  const disableAcoesPorSituacao = pagamentoTravado;
  const readOnlyPage = readOnlyFromList || disableAcoesPorSituacao;
  const disableGerar = readOnlyPage;

  const disableGerarReason = useMemo(() => {
    if (readOnlyFromList) return "Somente visualização.";
    if (disableAcoesPorSituacao)
      return "Ações bloqueadas: pagamento já gerado, provisionado ou concluído.";
    return "";
  }, [readOnlyFromList, disableAcoesPorSituacao]);

  const totalTitulos = useMemo(() => {
    const arr = detalhe?.titulosDeduzidos ?? [];
    return arr.reduce((acc, t) => acc + (Number(t?.valor) || 0), 0);
  }, [detalhe]);

  const totalOutras = useMemo(() => {
    const arr = detalhe?.outrasDeducoes ?? [];
    return arr.reduce((acc, t) => acc + (Number(t?.valor) || 0), 0);
  }, [detalhe]);

  const parcelaInitialData = useMemo(
    () => ({
      descricao: detalhe?.descricao,
      condicaoPagamento: detalhe?.condicoesPagamento?.length
        ? detalhe.condicoesPagamento
        : detalhe?.condicaoPagamento,
      condicaoPagamentoId: detalhe?.condicaoPagamentoId,
      dataEmissao: detalhe?.dataEmissao,
      dataVencimento: detalhe?.dataVencimento,
      numeroPAG: detalhe?.numeroPAG,
      dataAprovacaoPAG: detalhe?.dataAprovacaoPAG,
      numeroTitulo: detalhe?.numeroTitulo,
      titulo: detalhe?.numeroTitulo,
    }),
    [detalhe],
  );

  const situacaoFromList = String(location?.state?.situacao || "");
  const solicitacaoFromList = Number(location?.state?.solicitacao) || 0;
  const anoFromList = Number(location?.state?.selectedYear) || 0;

  const fetchData = useCallback(
    async (opts: { showSpinner?: boolean } = {}) => {
      const showSpinner = opts?.showSpinner !== false;
      if (showSpinner) setIsLoading(true);
      setErro(null);

      try {
        const comprovanteDespesasId = Number(idSolicitacao);
        if (!comprovanteDespesasId || Number.isNaN(comprovanteDespesasId)) {
          throw new Error("ID do comprovante de despesas inválido na URL.");
        }

        const raw = await getPagamentoComprovanteDespesasById(
          comprovanteDespesasId,
        );
        const data = mapPagamentoComprovanteToDetail(raw, {
          situacao: situacaoFromList,
          solicitacao: solicitacaoFromList,
          ano: anoFromList,
        });
        setDetalhe(data);
        setFormPagamento({
          descricao: data.descricao || "",
          condicao: data.condicaoPagamento || "10 dias",
          condicaoPagamentoId: Number(data.condicaoPagamentoId) || 0,
        });
        return true;
      } catch (error: unknown) {
        console.error("Erro ao carregar programação de pagamento:", error);
        setErro(error);
        return false;
      } finally {
        if (showSpinner) setIsLoading(false);
      }
    },
    [idSolicitacao, situacaoFromList, solicitacaoFromList, anoFromList],
  );

  useEffect(() => {
    fetchData({ showSpinner: true });
  }, [fetchData]);

  const handleBaixarFormulario = async (e) => {
    e.preventDefault();
    if (downloadingForm) return;

    const pagamentoId = Number(detalhe?.idPagamento);
    if (!pagamentoId || Number.isNaN(pagamentoId)) {
      showMsg("ID do pagamento não encontrado. Recarregue a página.", "error");
      return;
    }

    setDownloadingForm(true);
    try {
      await downloadPagamentoComprovanteDespesasArquivoAssinado(
        pagamentoId,
        `formulario-comprovante-${detalhe?.idSolicitacao ?? pagamentoId}.pdf`,
      );
      showMsg("Formulário de comprovante baixado.", "success");
    } catch (err) {
      console.error("Erro ao baixar formulário:", err);
      showMsg("Erro ao baixar formulário.", "error");
    } finally {
      setDownloadingForm(false);
    }
  };

  const handleGerarPagamento = async (
    opts: { showSpinner?: boolean } = {},
  ) => {
    if (disableGerar) {
      showMsg(disableGerarReason || "Ações bloqueadas.", "warning");
      return false;
    }

    const pagamentoId = Number(detalhe?.idPagamento);
    if (!pagamentoId || Number.isNaN(pagamentoId)) {
      showMsg("ID do pagamento não encontrado. Recarregue a página.", "error");
      throw new Error("ID do pagamento inválido.");
    }

    const condicaoPagamentoId = Number(formPagamento.condicaoPagamentoId);
    if (!condicaoPagamentoId) {
      showMsg("Condição de pagamento inválida. Verifique o cadastro.", "error");
      throw new Error("Condição de pagamento inválida.");
    }

    try {
      await gerarPagPagamentoDespesasAvaliacao(pagamentoId, {
        condicaoPagamentoId,
      });
      navigate(PATHS.PAYMENT_ROOT, {
        state: {
          toastMessage: "PAG gerado com sucesso.",
          toastType: "success",
        },
      });
      return true;
    } catch (error) {
      console.error("Erro ao gerar PAG:", error);
      showMsg("Erro ao gerar PAG. Tente novamente.", "error");
      throw error;
    }
  };

  if (isLoading) return <PageLoading minHeight="80vh" />;

  if (erro || !detalhe) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          Ocorreu um problema ao carregar os dados. Tente novamente.
        </div>

        <button
          className="btn btn-primary"
          onClick={() => fetchData({ showSpinner: true })}
        >
          Recarregar
        </button>

        <NotificationToast
          show={showToast}
          onClose={() => setShowToast(false)}
          message={toastMessage}
          type={toastType}
        />
      </div>
    );
  }

  const selectedYear =
    Number(location?.state?.selectedYear) || detalhe.ano || 2026;

  return (
    <div className="container mt-4 pagamento-container">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <h2 className="text-primary fw-bold m-0 pagamento-title-page">
          PROGRAMAÇÃO DE PAGAMENTO
        </h2>

        <div className="pagamento-header-right">
          <button
            type="button"
            className="merit-voltar-seta seta-voltar"
            onClick={() => navigate(PATHS.PAYMENT_ROOT)}
            title="Voltar"
            aria-label="Voltar"
          >
            <FaArrowLeft size={20} />
          </button>
          <span className="pagamento-subtitulo">
            {formatSolicitacao(detalhe.solicitacao)} - {selectedYear}
          </span>
        </div>
      </div>

      <DadosSindicato
        DadosSindicato={{
          cnpj: detalhe.cnpj,
          nome: detalhe.nomeSindicato,
          nomeSindicato: detalhe.nomeSindicato,
          sigla: detalhe.sigla,
        }}
      />

      <div className="card shadow-sm p-4 mb-4 card-rounded">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold text-uppercase m-0 text-primary">Valores</h5>
          <button
            className="btn btn-primary btn-baixar-formulario d-flex align-items-center gap-2"
            disabled={downloadingForm}
            onClick={handleBaixarFormulario}
            type="button"
            title="Formulário de comprovante assinado pelo presidente"
          >
            {downloadingForm ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span>Gerando...</span>
              </>
            ) : (
              <>
                <FaDownload size={14} />
                BAIXAR FORMULÁRIO
              </>
            )}
          </button>
        </div>

        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label fw-semibold">VALOR BRUTO DO APOIO</label>
            <input
              type="text"
              className="form-control"
              value={formatCurrency(detalhe.valorBrutoApoio)}
              disabled
              readOnly
            />
          </div>

          <div className="col-md-4 mb-3">
            <label className="form-label fw-semibold">DEDUÇÕES</label>
            <input
              type="text"
              className="form-control"
              value={formatCurrency(detalhe.valorDeducoes)}
              disabled
              readOnly
            />
          </div>

          <div className="col-md-4 mb-3">
            <label className="form-label fw-semibold">
              TOTAL LÍQUIDO DA SOLICITAÇÃO
            </label>
            <input
              type="text"
              className="form-control"
              value={formatCurrency(detalhe.valorLiquido)}
              disabled
              readOnly
            />
          </div>
        </div>
      </div>

      <TabelaReembolso
        mode="titulos"
        titulos={detalhe.titulosDeduzidos}
        totalDeduzir={totalTitulos}
        readOnly={true}
      />

      <OutrasDeducoes
        itens={detalhe.outrasDeducoes}
        totalDeduzir={totalOutras}
      />

      <ParcelaDados
        title="Dados do Pagamento"
        readOnly={readOnlyPage}
        initialData={parcelaInitialData}
        lockCondicao={readOnlyPage}
        onChange={(form) => {
          if (readOnlyPage) return;
          setFormPagamento((prev) => ({
            ...prev,
            descricao: String(form?.descricao ?? prev.descricao),
            condicao: String(form?.condicao ?? prev.condicao),
            condicaoPagamentoId: Number(
              form?.condicaoPagamentoId ?? prev.condicaoPagamentoId,
            ),
          }));
        }}
      />

      <AcoesFormulario
        onGerarPagamento={handleGerarPagamento}
        disableGerar={disableGerar}
        disableGerarReason={disableGerarReason}
      />

      <NotificationToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type={toastType}
      />
    </div>
  );
};

export default Pagamento;
